import { z } from "zod";

// Contact form schema
export const contactSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Name is required")
    .max(100, "Name must be less than 100 characters"),
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),
  company: z
    .string()
    .trim()
    .max(100, "Company name must be less than 100 characters")
    .optional()
    .or(z.literal("")),
  message: z
    .string()
    .trim()
    .min(1, "Message is required")
    .max(2000, "Message must be less than 2000 characters"),
});

export type ContactFormData = z.infer<typeof contactSchema>;

// Early access form schema
export const earlyAccessSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),
  userType: z
    .enum(["brand", "sorting-facility", "remaker"], {
      required_error: "Please select your role",
      invalid_type_error: "Please select a valid role",
    }),
  gdprConsent: z.literal(true, {
    errorMap: () => ({ message: "You must accept the privacy policy to continue" }),
  }),
});

export type EarlyAccessFormData = z.infer<typeof earlyAccessSchema>;

// Newsletter form schema
export const newsletterSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),
  gdprConsent: z.literal(true, {
    errorMap: () => ({ message: "You must accept the privacy policy to continue" }),
  }),
});

export type NewsletterFormData = z.infer<typeof newsletterSchema>;

// Auth sign-in schema
export const signInSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),
  password: z
    .string()
    .min(1, "Password is required"),
});

export type SignInFormData = z.infer<typeof signInSchema>;

// Auth sign-up schema
export const signUpSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(1, "Full name is required")
    .max(100, "Name must be less than 100 characters"),
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),
  company: z
    .string()
    .trim()
    .max(100, "Company name must be less than 100 characters")
    .optional()
    .or(z.literal("")),
  role: z
    .enum(["brand", "designer"], {
      required_error: "Please select your role",
      invalid_type_error: "Please select a valid role",
    }),
  password: z
    .string()
    .min(6, "Password must be at least 6 characters"),
});

export type SignUpFormData = z.infer<typeof signUpSchema>;

// Helper to get first error message from Zod validation
export const getFirstError = <T>(result: z.SafeParseReturnType<T, T>): string => {
  if (!result.success) {
    return result.error.errors[0]?.message || "Validation failed";
  }
  return "Validation failed";
};

// Helper to get all field errors from Zod validation
export const getFieldErrors = <T>(
  result: z.SafeParseReturnType<T, T>
): Record<string, string> => {
  if (result.success) return {};
  
  const errors: Record<string, string> = {};
  for (const error of result.error.errors) {
    const field = error.path[0];
    if (field && typeof field === "string" && !errors[field]) {
      errors[field] = error.message;
    }
  }
  return errors;
};

// Material request schema
export const materialRequestSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),
  intent: z.enum(["sell", "source"], {
    required_error: "Please select whether you want to sell or source material",
    invalid_type_error: "Please select a valid intent",
  }),
  description: z
    .string()
    .trim()
    .min(1, "Please describe the materials you have or need")
    .max(1000, "Description must be less than 1000 characters"),
  gdprConsent: z.literal(true, {
    errorMap: () => ({ message: "You must accept the privacy policy to continue" }),
  }),
});

export type MaterialRequestFormData = z.infer<typeof materialRequestSchema>;

// Type for field errors
export type FieldErrors = Record<string, string>;
